import React from "react";

import { useTranslation } from "react-i18next";
import { decodeToken } from "react-jwt";
import { Redirect, useHistory } from "react-router-dom";

import { PRIVATE_ROUTES, PUBLIC_ROUTES, ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { Resources } from "../models/resources";
import { setupMUI } from "../util/app/setup-mui";
import { CompanyProfile, OnboardingStage, Routes } from "../util/enums";
import { getUrlToRedirect } from "../util/getUrlToRedirect";
import { links } from "../util/links";
import { PrivateRoutes } from "./Routes/PrivateRoutes";
import { PublicRoutes } from "./Routes/PublicRoutes";
import UsertourBootstrap from "../components/UsertourBootstrap";
import { DumontProvider } from "../context/DumontContext";

setupMUI();

export const MainApp = () => {
  const { currentUser, userUid } = useAppState();

  const { t: translate } = useTranslation();

  const changeTitlePageByResource = (resource: string) => {
    let tabNameFromLinks = "";
    const pathName = resource;

    if (currentUser?.company?.profile) {
      const linksByCompany = links[currentUser?.company?.profile?.initials];
      const linksGroups = linksByCompany ? Object.keys(linksByCompany) : [];

      if (linksByCompany && linksGroups && linksGroups?.length > 0) {
        linksGroups?.forEach((group) => {
          linksByCompany?.[group]?.forEach((link) => {
            const resourceRegex = new RegExp(`\\b${pathName.replace("-mnt", "")}\\b`, "gm");

            if (link?.path.match(resourceRegex)) {
              tabNameFromLinks = link.menuTitle;
            }
          });
        });
      }
    }

    const app = "Shipeezi";

    if (!resource) {
      document.title = app;

      return;
    }
    let path = String(resource);

    if (path.includes("-")) {
      const paths = resource.split("-");

      [path] = paths;
    }

    // Tratar caso especial do onboarding (é um objeto no arquivo de tradução)
    let titlePath: string;
    if (path === "onboarding") {
      titlePath = translate("onboarding.pageTitle");
    } else {
      titlePath = tabNameFromLinks ? translate(`${tabNameFromLinks}`) : translate(`${path}`);
    }

    const title = `${titlePath} | ${app}`;

    document.title = title;
  };

  const checkIfAllowedEnterScreen = (resources: Resources[], pathnameArray: string[]) => {
    let found = false;

    for (const resource of resources) {
      if (found) break;

      if (!resource.roles_resources) {
        return false;
      }

      const {
        // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
        roles_resources: { createdAt, updatedAt, valid, roleId, resourceId, ...options },
      } = resource;

      for (let path of pathnameArray) {
        if (found) break;
        if (path !== "") {
          for (const key of Object.keys(options) as (keyof typeof options)[]) {
            if (found) break;
            if (options[key]) {
              const name = resource.name.toLowerCase();

              path = path.toLowerCase();
              if (
                name.includes(path) ||
                path.includes(name) ||
                path.includes(Routes.PROFILE) ||
                path.includes(Routes.NOTIFICATIONS) ||
                path.includes(Routes.ON_BOARDING) ||
                path.includes(Routes.STORAGE_ACCOUNTS) ||
                path.includes(Routes.STORAGE_BOX_AND_UNIT) ||
                path.includes(Routes.ROUTE_PLANNER)
              ) {
                changeTitlePageByResource(path);
                found = true;
                break;
              }
              let { group } = resource;

              group = group.toLowerCase();
              if (group.includes(path) || path.includes(group)) {
                changeTitlePageByResource(path);
                found = true;
                break;
              }
            }
          }
        }
      }
    }

    return found;
  };

  const checkIfHasResourceToEnterScreen = (path: string, resources: Resources[]) => {
    for (const resource of resources) {
      const rName = resource.name.toLowerCase();
      const rGroup = resource.group.toLowerCase();

      path = path.toLowerCase();
      if (
        rName.includes(path) ||
        rGroup.includes(path) ||
        path.includes(rName) ||
        path.includes(rGroup)
      ) {
        if (!resource.roles_resources) return false;
        const {
          // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
          roles_resources: { createdAt, updatedAt, valid, roleId, resourceId, ...options },
        } = resource;

        for (const key of Object.keys(options) as (keyof typeof options)[]) {
          if (options[key]) {
            changeTitlePageByResource(path);

            return true;
          }
        }
      }
    }

    return false;
  };

  const encounterRouteOfSystemAndRedirect = (resources: Resources[]) => {
    for (const route of ROUTES) {
      const { path } = route;

      if (path.includes("-")) continue;
      for (const resource of resources) {
        const rName = resource.name.toLowerCase();
        const rGroup = resource.group.toLowerCase();

        if (
          rName.includes(path) ||
          rGroup.includes(path) ||
          path.includes(rName) ||
          path.includes(rGroup)
        ) {
          if (!resource.roles_resources) return <Redirect to={PRIVATE_ROUTES.UNAUTHORIZED} />;

          const {
            // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
            roles_resources: { createdAt, updatedAt, valid, roleId, resourceId, ...options },
          } = resource;

          for (const key of Object.keys(options) as (keyof typeof options)[]) {
            if (options[key]) {
              changeTitlePageByResource(path);

              return <Redirect to={`/app/${path}`} />;
            }
          }
        }
      }
    }

    return <Redirect to={PRIVATE_ROUTES.UNAUTHORIZED} />;
  };

  const history = useHistory();

  const onboardingIsCompleted = () => {
    if (
      currentUser &&
      currentUser?.company &&
      currentUser?.company?.onboardingStage &&
      currentUser?.company?.onboardingStage?.stage
    ) {
      if (currentUser?.company?.profile?.initials === CompanyProfile.ST) {
        if (
          currentUser?.company?.onboardingStage?.stage < OnboardingStage.SHIPMENT_TRACKING_FINISHED
        )
          return false;
      } else if (currentUser?.company?.onboardingStage?.stage < OnboardingStage.FINISHED)
        return false;

      return true;
    }

    return true;
  };
  const validateToken = () => {
    if (userUid) {
      const decoded: { timeout?: Date } | null = decodeToken(userUid?.split(" ")?.[1] || "");

      if (decoded && decoded?.timeout) {
        const tokenFinish = new Date(decoded?.timeout).getTime();
        const now = new Date().getTime();

        if (tokenFinish < now) return false;

        return true;
      }
    }

    return false;
  };
  const pathnameArray = history.location.pathname.split("/");

  if (history.location.pathname === "/") {
    if (!userUid || !validateToken()) {
      return <Redirect to={`/signin${getUrlToRedirect()}`} />;
    }

    if (currentUser) {
      if (!currentUser.verified) return <Redirect to={PUBLIC_ROUTES.UPDATE_PASSWORD} />;

      if (
        !pathnameArray.includes(Routes.ON_BOARDING) &&
        !onboardingIsCompleted() &&
        currentUser?.verified
      )
        return <Redirect to={PRIVATE_ROUTES.ONBOARDING} />;

      const { resources } = currentUser;
      const profileId = currentUser?.company?.profileId;

      switch (profileId) {
        case 1:
        case 2:
        case 3:
          if (checkIfHasResourceToEnterScreen("overview", resources))
            return <Redirect to={`${PRIVATE_ROUTES.OVERVIEW_MAIN}`} />;

          return encounterRouteOfSystemAndRedirect(resources);
        case 6:
          if (checkIfHasResourceToEnterScreen("orders", resources))
            return <Redirect to={`${PRIVATE_ROUTES.ORDERS}`} />;
        default:
          return encounterRouteOfSystemAndRedirect(resources);
      }
    }
  } else if (userUid) {
    let found = false;
    if (
      currentUser &&
      !currentUser?.verified &&
      !pathnameArray.includes(Routes.UPDATE_PASSWORD) &&
      !pathnameArray.includes(Routes.SIGNIN)
    ) {
      return <Redirect to={PUBLIC_ROUTES.UPDATE_PASSWORD} />;
    }

    if (
      currentUser?.verified &&
      !pathnameArray.includes(Routes.ON_BOARDING) &&
      !onboardingIsCompleted()
    )
      return <Redirect to={PRIVATE_ROUTES.ONBOARDING} />;

    if (currentUser) {
      const { resources } = currentUser;

      if (pathnameArray.includes(Routes.ON_BOARDING) && onboardingIsCompleted()) {
        return encounterRouteOfSystemAndRedirect(resources);
      }
    }

    if (currentUser)
      found =
        checkIfAllowedEnterScreen(currentUser?.resources, pathnameArray) ||
        pathnameArray.includes(Routes.ON_BOARDING) ||
        pathnameArray.includes(Routes.PROFILE) ||
        pathnameArray.includes(Routes.NOTIFICATIONS) ||
        pathnameArray.includes(Routes.FORGOT) ||
        pathnameArray.includes(Routes.UPDATE_PASSWORD) ||
        pathnameArray.includes(Routes.APPROVE_COMPANY_TEMPORARY) ||
        pathnameArray.includes(Routes.ACCEPT_INVITE) ||
        pathnameArray.includes(Routes.PRIVACY_POLICY) ||
        pathnameArray.includes(Routes.CUSTOMER_PORTAL) ||
        pathnameArray.includes(Routes.ESTIMATE_QUOTE);

    if (currentUser) {
      if (
        (pathnameArray.includes(PUBLIC_ROUTES.SIGN_UP.split("/")[1]) ||
          pathnameArray.includes(PUBLIC_ROUTES.SIGN_IN.split("/")[1]) ||
          pathnameArray.includes(PUBLIC_ROUTES.FORGOT.split("/")[1])) &&
        onboardingIsCompleted()
      ) {
        const profileId = currentUser?.company?.profileId;

        switch (profileId) {
          case 1:
          case 2:
          case 3:
            return <Redirect to={`${PRIVATE_ROUTES.OVERVIEW_MAIN}`} />;
          case 6:
            return <Redirect to={`${PRIVATE_ROUTES.ORDERS}`} />;
          default:
            return <Redirect to={PRIVATE_ROUTES.OVERVIEW_MAIN} />;
        }
      }
    }

    // if (
    //   !found &&
    //   !history.location.pathname.includes("unauthorized") &&
    //   !history.location.pathname.includes("signin")
    // ) {
    //   return <Redirect to={PRIVATE_ROUTES.UNAUTHORIZED} />;
    // }
  } else if (
    history.location.pathname.includes("/app") &&
    !history.location.pathname.includes("approve")
  ) {
    return <Redirect to={`/signin${getUrlToRedirect()}`} />;
  }

  return (
    <DumontProvider>
      <UsertourBootstrap />
      <PublicRoutes />
      <PrivateRoutes />
    </DumontProvider>
  );
};
